import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Pencil, Users, ExternalLink, Trash2, Power, CalendarDays, Building2, UserRound, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { errMsg, formatFairDate } from '@/components/jobfairs/fairApi';
import { NewFairDialog } from './NewFairDialog';

export const STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  active: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  completed: 'bg-slate-800 text-white border-slate-700',
};

export const StatusBadge = ({ fair }) => {
  const s = fair.effective_status;
  const label = s === 'completed' && fair.status === 'deactivated' ? 'Deactivated · Completed' : s;
  return <span data-testid={`jobfair-status-${fair.slug}`} className={`inline-flex px-2.5 py-0.5 rounded-full border text-xs font-semibold capitalize ${STATUS_STYLE[s]}`}>{label}</span>;
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white rounded-xl border p-4 flex items-center gap-3" data-testid={testId}>
    <span className="w-10 h-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center"><Icon className="w-5 h-5" /></span>
    <div><p className="text-2xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
  </div>
);

export default function JobFairList({ openNew = false }) {
  const navigate = useNavigate();
  const [fairs, setFairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(openNew);

  const load = useCallback(() => {
    api.get('/job-fairs/admin/list').then((r) => setFairs(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);
  useEffect(() => setDlg(openNew), [openNew]);

  const setStatus = async (f, status) => {
    try { await api.put(`/job-fairs/admin/${f.id}/status`, { status }); toast.success(`Job fair ${status === 'active' ? 'activated' : status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? Its public page will stop working.`)) return;
    try { await api.delete(`/job-fairs/admin/${f.id}`); toast.success('Deleted'); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const total = (k) => fairs.reduce((a, f) => a + (f[k] || 0), 0);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="jobfair-list-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-slate-900">Manage Job Fairs</h1><p className="text-sm text-slate-500">Standalone job fair mini-sites with their own registrations</p></div>
        <Button onClick={() => setDlg(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="jobfair-new-button"><Plus className="w-4 h-4 mr-1" /> New Job Fair</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={CalendarDays} label="Total Job Fairs" value={fairs.length} testId="jobfair-stat-total" />
        <Stat icon={Rocket} label="Active Fairs" value={fairs.filter((f) => f.effective_status === 'active').length} testId="jobfair-stat-active" />
        <Stat icon={UserRound} label="Candidates Registered" value={total('candidate_count')} testId="jobfair-stat-candidates" />
        <Stat icon={Building2} label="Employers Registered" value={total('employer_count')} testId="jobfair-stat-employers" />
      </div>
      {loading ? <p className="text-slate-500">Loading…</p> : fairs.length === 0 ? (
        <div className="bg-white border rounded-xl p-12 text-center text-slate-500" data-testid="jobfair-empty">No job fairs yet. Click “New Job Fair” to create one.</div>
      ) : (
        <div className="grid gap-4">
          {fairs.map((f) => (
            <div key={f.id} data-testid={`jobfair-row-${f.slug}`} className="bg-white border rounded-xl p-5 flex flex-col lg:flex-row lg:items-center gap-4 hover:shadow-md transition-shadow">
              <img src={f.logo} alt="" className="w-14 h-14 object-contain rounded-lg border bg-white shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2"><p className="font-semibold text-slate-900 text-lg">{f.name}</p><StatusBadge fair={f} /></div>
                <p className="text-sm text-slate-500 mt-0.5">{[formatFairDate(f), f.venue_name, f.city].filter(Boolean).join(' · ') || 'Dates not set'}</p>
                <a href={`/jobfairs/${f.slug}`} target="_blank" rel="noreferrer" className="text-sm text-teal-700 hover:underline inline-flex items-center gap-1" data-testid={`jobfair-public-link-${f.slug}`}>/jobfairs/{f.slug} <ExternalLink className="w-3 h-3" /></a>
              </div>
              <div className="flex gap-6 text-center">
                <div><p className="text-xl font-bold" data-testid={`jobfair-candidates-${f.slug}`}>{f.candidate_count || 0}</p><p className="text-xs text-slate-500">Candidates</p></div>
                <div><p className="text-xl font-bold" data-testid={`jobfair-employers-${f.slug}`}>{f.employer_count || 0}</p><p className="text-xs text-slate-500">Employers</p></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/admin/jobfairs/${f.id}/edit`)} data-testid={`jobfair-edit-${f.slug}`}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
                <Button size="sm" variant="outline" asChild data-testid={`jobfair-registrations-${f.slug}`}><Link to={`/admin/jobfairs/${f.id}/registrations`}><Users className="w-4 h-4 mr-1" />Registrations</Link></Button>
                {f.status !== 'active'
                  ? <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(f, 'active')} data-testid={`jobfair-activate-${f.slug}`}><Power className="w-4 h-4 mr-1" />Activate</Button>
                  : <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => setStatus(f, 'deactivated')} data-testid={`jobfair-deactivate-${f.slug}`}><Power className="w-4 h-4 mr-1" />Deactivate</Button>}
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(f)} data-testid={`jobfair-delete-${f.slug}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <NewFairDialog open={dlg} onOpenChange={(v) => { setDlg(v); if (!v && openNew) navigate('/admin/jobfairs'); }} />
    </div>
  );
}
